import axios from 'axios';
import Swal from 'sweetalert2';
import { useAuthStorage } from '../hooks/useAuthStorage';

const { getToken, clearToken, isTokenExpired } = useAuthStorage();

export const endSession = () => {
  clearToken();
  const currentPath = window.location.pathname;
  if (currentPath !== '/signin') {
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    window.location.href = '/signin';
  }
};

// Sign out as soon as the JWT expires, even if the app is idle.
setInterval(() => {
  const token = getToken();
  if (token && isTokenExpired(token)) endSession();
}, 60_000);

// Configuracion base de axios para la plataforma
axios.defaults.baseURL = import.meta.env.VITE_BASE_URL;

// Request interceptor - Agregar token a las peticiones
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      // Don't send a request we know will 401 — repeated 401s get the IP banned.
      if (isTokenExpired(token)) {
        endSession();
        return Promise.reject(new axios.CanceledError('Session expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Manejar errores de respuesta
axios.interceptors.response.use(
  // Respuestas exitosas (2xx) pasan directamente
  (response) => response,
  
  // Manejar errores de respuesta
  (error) => {
    // Error de red (sin respuesta del servidor)
    if (!error.response) {
      console.error('Error de red:', error.message);
      
      // Mostrar alerta de error de red
      Swal.fire({
        title: 'Error de Conexión',
        text: 'Por favor verifica tu conexión a internet.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#3085d6',
      });
      
      return Promise.reject({
        message: 'Error de conexión. Por favor verifica tu conexión a internet.',
        type: 'network_error',
        originalError: error
      });
    }

    const { status, data } = error.response;

    switch (status) {
      case 401:
        // No autorizado - Token inválido o expirado
        console.warn('Usuario no autorizado. Redirigiendo al login...');
        
        endSession();

        return Promise.reject({
          message: data?.message || 'Sesión expirada. Por favor inicia sesión nuevamente.',
          type: 'unauthorized',
          status: 401,
          originalError: error
        });

      case 403:
        // Prohibido - El usuario no tiene permisos
        console.warn('Acceso prohibido:', data?.message);
        return Promise.reject({
          message: data?.message || 'No tienes permisos para realizar esta acción.',
          type: 'forbidden',
          status: 403,
          originalError: error
        });

      case 404:
        // Recurso no encontrado
        return Promise.reject({
          message: data?.message || 'El recurso solicitado no fue encontrado.',
          type: 'not_found',
          status: 404,
          originalError: error
        });

      case 422:
        // Error de validación
        return Promise.reject({
          message: data?.message || 'Error de validación en los datos enviados.',
          type: 'validation_error',
          status: 422,
          errors: data?.errors || {},
          originalError: error
        });

      case 429:
        // Demasiadas peticiones
        return Promise.reject({
          message: data?.message || 'Demasiadas peticiones. Por favor intenta más tarde.',
          type: 'rate_limit',
          status: 429,
          originalError: error
        });

      case 500:
      case 502:
      case 503:
      case 504:
        // Errores del servidor
        // console.error('Error del servidor:', status, data);
        return Promise.reject({
          message: data?.message || 'Error del servidor. Por favor intenta más tarde.',
          type: 'server_error',
          status,
          originalError: error
        });

      default:
        // Otros errores
        return Promise.reject({
          message: data?.message || 'Ha ocurrido un error inesperado.',
          type: 'unknown_error',
          status,
          originalError: error
        });
    }
  }
);

// Instancia de axios sin interceptores para request password reset y otros
const axiosClean = axios.create({});

export default axiosClean;
